<script lang="ts">
	// T21: the owner removed this till from its shop (#382, N16). It covers
	// the whole till, above the lock screens, Suspended (T13) and every sheet,
	// and nothing beneath it can be tapped. It has no action: the till sells
	// no more, and its order store still sends every sale it holds to Bosla,
	// on the stale track. It says how many sales have not reached Bosla yet,
	// so nobody wipes the device while they wait, and how many open orders
	// stay on it, which the staff settle on another till.
	//
	// It clears on its own only if Bosla takes the till's key again.

	import BrandMark from '../components/BrandMark.svelte';
	import Card from '../components/Card.svelte';
	import Icon from '../components/Icon.svelte';
	import { message, type Language } from '../i18n/messages';
	import { countLine } from '../till/storeState.svelte';

	interface Props {
		language: Language;
		/** Restaurant · shop · till, when the till knows them. */
		where: string;
		/** Closed sales Bosla has not confirmed, and open orders, on this till. */
		waiting: number;
		openOrders: number;
	}

	let { language, where, waiting, openOrders }: Props = $props();

	// Focus moves into the cover, so a keyboard or a screen reader is not
	// left on the inert screen beneath it.
	let cover: HTMLDivElement | undefined = $state();
	$effect(() => {
		cover?.focus();
	});
</script>

<div
	class="cover"
	role="alertdialog"
	aria-modal="true"
	aria-label={message(language, 'removedLabel')}
	tabindex="-1"
	bind:this={cover}
	data-testid="till-removed"
>
	<div class="body">
		<BrandMark size={26} />
		<div class="head">
			{#if where}
				<span class="where" data-testid="till-removed-where">{where}</span>
			{/if}
			<h1>{message(language, 'removedTitle')}</h1>
			<p class="tell">{message(language, 'removedAsk')}</p>
		</div>
		<Card testId="till-removed-sales">
			<div class="lines">
				{#if waiting > 0}
					<div class="line strong" data-testid="till-removed-waiting">
						<span class="mark"><Icon name="clock" size={22} /></span>
						<p>
							{countLine(language, waiting, 'removedWaitOne', 'removedWaitTwo', 'removedWaitMany')}
						</p>
					</div>
				{:else}
					<div class="line strong" data-testid="till-removed-done">
						<span class="mark done"><Icon name="check" size={22} /></span>
						<p>{message(language, 'removedDone')}</p>
					</div>
				{/if}
				{#if openOrders > 0}
					<div class="line open" data-testid="till-removed-open">
						<span class="mark quiet"><Icon name="receipt" size={22} /></span>
						<p>
							{countLine(
								language,
								openOrders,
								'removedOpenOne',
								'removedOpenTwo',
								'removedOpenMany',
							)}
						</p>
					</div>
				{/if}
			</div>
		</Card>
		<p class="returns">{message(language, 'removedReturns')}</p>
	</div>
</div>

<style>
	.cover {
		outline: none;
		position: fixed;
		inset: 0;
		z-index: var(--z-cover);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--space-9);
		background: var(--surface-page);
		color: var(--text-strong);
		overflow-y: auto;
	}

	.body {
		inline-size: 100%;
		max-inline-size: 560px;
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.head {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.where {
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
		text-wrap: pretty;
	}

	.tell {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 600;
		color: var(--text-strong);
	}

	.lines {
		display: flex;
		flex-direction: column;
	}

	.line {
		display: flex;
		align-items: flex-start;
		gap: var(--space-4);
		padding: var(--card-pad);
	}

	.line p {
		margin: 0;
		line-height: var(--leading-snug);
		text-wrap: pretty;
	}

	.strong p {
		font-size: var(--text-md);
		font-weight: 600;
		color: var(--text-strong);
	}

	.open {
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.open p {
		font-size: var(--text-base);
		color: var(--text-body);
	}

	.mark {
		flex: none;
		padding-block-start: 2px;
		color: var(--text-strong);
	}

	.mark.done {
		color: var(--accent-visibility);
	}

	.mark.quiet {
		color: var(--text-body);
	}

	.returns {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-muted);
		text-wrap: pretty;
	}
</style>
